import React from 'react';
import { ListSubheader } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { FuseUtils } from '@fuse';
import { withRouter } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import NavCollapse from './FuseNavCollapse';
import NavItem from './FuseNavItem';
import NavLink from './FuseNavLink';
import { isParticipant } from 'app/main/util/enums';

const useStyles = makeStyles({
	item: {
		height: 40,
		width: 'calc(100% - 16px)',
		borderRadius: '0 20px 20px 0',
		paddingRight: 12
	}
});

const NavGroup = props => {
	const userRole = useSelector(({ auth }) => auth.user.role);
	const user = useSelector(({ auth }) => auth.user);

	const classes = useStyles(props);
	const { item, nestedLevel, active } = props;
	let paddingValue = 40 + nestedLevel * 16;
	const listItemPadding = nestedLevel > 0 ? 'pl-' + (paddingValue > 80 ? 80 : paddingValue) : 'pl-24';

	if (!FuseUtils.hasPermission(item.auth, userRole)) {
		return null;
	}

	// Check if any children are visible
	const hasVisibleChildren = (children) => {
		if (!children || children.length === 0) return false;
		
		return children.some(child => {
			// Check basic authorization
			if (!FuseUtils.hasPermission(child.auth, userRole)) {
				// If no auth specified, continue checking
				if (child.auth !== undefined) {
					return false;
				}
			}
			
			// If it's a group or collapse, check if it has visible children
			if (child.type === 'group' || child.type === 'collapse') {
				return hasVisibleChildren(child.children);
			}
			
			// For leaf items, check training_allow for participants
			if (child.type === 'item') {
				if (isParticipant(user) && !child.training_allow) {
					return false;
				}
			}
			
			// If we get here, the item is visible
			return true;
		});
	};

	// If no visible children, don't render the group
	if (!hasVisibleChildren(item.children)) {
		return null;
	}

	return (
		<React.Fragment>
			<ListSubheader
				disableSticky={true}
				className={clsx(classes.item, listItemPadding, 'list-subheader flex items-center')}
			>
				<span className="list-subheader-text uppercase text-12">{item.title}</span>
			</ListSubheader>

			{item.children && (
				<React.Fragment>
					{item.children.map(item => (
						<React.Fragment key={item.id}>
							{item.type === 'group' && <NavGroup item={item} nestedLevel={nestedLevel} active={active} />}

							{item.type === 'collapse' && <NavCollapse item={item} nestedLevel={nestedLevel} active={active} />}

							{item.type === 'item' && <NavItem item={item} nestedLevel={nestedLevel} active={active} />}

							{item.type === 'link' && <NavLink item={item} nestedLevel={nestedLevel} active={active} />}
						</React.Fragment>
					))}
				</React.Fragment>
			)}
		</React.Fragment>
	);
};

NavGroup.propTypes = {
	item: PropTypes.shape({
		id: PropTypes.string.isRequired,
		title: PropTypes.string,
		children: PropTypes.array
	})
};

NavGroup.defaultProps = {};

export default withRouter(React.memo(NavGroup));
